import * as Sentry from '@sentry/browser';
import { ContentType, Resume, SectionType } from '@sr/types';
import React from 'react';
import {
  AddComboButton,
  Box,
  BulbIcon,
  Button,
  CameraIcon,
  ConferenceIcon,
  DetailListSectionIcon,
  GraduationIcon,
  Grid,
  GroupedSkillsIcon,
  HandIcon,
  LanguageIcon,
  NModal,
  PatentsIcon,
  ProjectsIcon,
  ReferencesIcon,
  StandardSectionIcon,
  SuitcaseIcon,
  TagListSectionIcon,
  Text,
} from '../../ui';
import API from '../../utils/api';

type Props = {
  resume: Resume;
  closeDrawer?: () => void;
};

const AddSection = ({ resume, closeDrawer }: Props) => (
  <NModal OpenButton={<OpenButton />} title="Add a Section">
    <ModalContents resume={resume} closeDrawer={closeDrawer} />
  </NModal>
);

// The drawer here is the write page sidebar nav, that contains this modal
// We want to close both when a section is added.
function ModalContents({
  closeModal,
  closeDrawer,
  resume,
}: {
  closeModal?: () => void;
  closeDrawer?: () => void;
  resume: Resume;
}) {
  function createSection({
    type,
    contentType,
    title,
  }: {
    type: SectionType;
    contentType: ContentType;
    title: string;
  }) {
    API.section
      .create({
        resume,
        type,
        contentType,
        title,
      })
      .then((sectionId) => {
        closeModal && closeModal();
        closeDrawer && closeDrawer();
        setTimeout(() => {
          const targetEl = document.querySelector(`#nav-${sectionId}`);
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
        API.events.logEvent({
          userId: resume.owner,
          eventName: 'Resume Section Added',
          properties: {
            Type: type,
            'Content Type': contentType,
            Title: title,
          },
        });
      })
      .catch((error) => {
        Sentry.captureException(error);
        alert('There was an error adding the section.');
      });
  }
  return (
    <Box id="gi-add-section-modal">
      <Text color="black50" mb={3}>
        Common sections
      </Text>
      <Grid variant="sectionPicker">
        <Button
          variant="tileIcon"
          data-testid="add-section-work"
          onClick={() => {
            createSection({
              title: 'Work Experience',
              type: 'detail',
              contentType: 'work-experience',
            });
          }}
        >
          <SuitcaseIcon size={40} />
          <Text variant="caption" mt={2}>
            Work Experience
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-education"
          onClick={() => {
            createSection({
              title: 'Education',
              type: 'detail',
              contentType: 'education',
            });
          }}
        >
          <GraduationIcon size={40} />
          <Text variant="caption" mt={2}>
            Education
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-languages"
          onClick={() => {
            createSection({
              title: 'Languages',
              type: 'block-list',
              contentType: 'languages',
            });
          }}
        >
          <LanguageIcon size={40} />
          <Text variant="caption" mt={2}>
            Languages
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-projects"
          onClick={() => {
            createSection({
              title: 'Projects',
              type: 'detail',
              contentType: 'projects',
            });
          }}
        >
          <ProjectsIcon size={40} />
          <Text variant="caption" mt={2}>
            Projects
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-references"
          onClick={() => {
            createSection({
              title: 'References',
              type: 'block-list',
              contentType: 'references',
            });
          }}
        >
          <ReferencesIcon size={40} />
          <Text variant="caption" mt={2}>
            References
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-patents"
          onClick={() => {
            createSection({
              title: 'Patents',
              type: 'block-list',
              contentType: 'patents',
            });
          }}
        >
          <PatentsIcon size={40} />
          <Text variant="caption" mt={2}>
            Patents
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-hobbies"
          onClick={() => {
            createSection({
              title: 'Hobbies',
              type: 'inline-list',
              contentType: 'hobbies',
            });
          }}
        >
          <CameraIcon size={40} />
          <Text variant="caption" mt={2}>
            Hobbies
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-skills"
          onClick={() => {
            createSection({
              title: 'Skills',
              type: 'inline-list',
              contentType: 'skills',
            });
          }}
        >
          <BulbIcon size={40} />
          <Text variant="caption" mt={2}>
            Skills
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-skills-grouped"
          onClick={() => {
            createSection({
              title: 'Skills',
              type: 'block-list',
              contentType: 'grouped-skills',
            });
          }}
        >
          <GroupedSkillsIcon size={40} />
          <Text variant="caption" mt={2}>
            Grouped Skills
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-internships"
          onClick={() => {
            createSection({
              title: 'Internships',
              type: 'detail',
              contentType: 'internships',
            });
          }}
        >
          <SuitcaseIcon size={40} />
          <Text variant="caption" mt={2}>
            Internships
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-volunteering"
          onClick={() => {
            createSection({
              title: 'Volunteering',
              type: 'detail',
              contentType: 'volunteering',
            });
          }}
        >
          <HandIcon size={40} />
          <Text variant="caption" mt={2}>
            Volunteering
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-conferences"
          onClick={() => {
            createSection({
              title: 'Conferences',
              type: 'block-list',
              contentType: 'conference',
            });
          }}
        >
          <ConferenceIcon size={40} />
          <Text variant="caption" mt={2}>
            Conferences
          </Text>
        </Button>
      </Grid>
      <Text color="black50" mb={3} mt={6}>
        Custom sections
      </Text>
      <Grid variant="sectionPicker">
        <Button
          variant="tileIcon"
          data-testid="add-section-custom-detail"
          onClick={() => {
            createSection({
              title: 'Custom Section',
              type: 'detail',
              contentType: 'custom',
            });
          }}
        >
          <StandardSectionIcon size={40} />
          <Text variant="caption" mt={2}>
            Standard
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-custom-inline"
          onClick={() => {
            createSection({
              title: 'Custom Section',
              type: 'inline-list',
              contentType: 'custom',
            });
          }}
        >
          <TagListSectionIcon size={40} />
          <Text variant="caption" mt={2}>
            Tag list
          </Text>
        </Button>
        <Button
          variant="tileIcon"
          data-testid="add-section-custom-block"
          onClick={() => {
            createSection({
              title: 'Custom Section',
              type: 'block-list',
              contentType: 'custom',
            });
          }}
        >
          <DetailListSectionIcon size={40} />
          <Text variant="caption" mt={2}>
            Detailed list
          </Text>
        </Button>
      </Grid>
    </Box>
  );
}

function OpenButton({ onClick }: { onClick?: () => void }) {
  return (
    <AddComboButton
      onClick={onClick}
      type="button"
      sx={{ pl: 0 }}
      id="gi-add-section-button"
      data-testid="open-add-section"
    >
      Add Section
    </AddComboButton>
  );
}
export default AddSection;
